import React from 'react';
import { Box, Divider, Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { formatPrice as formatNodeListPrice } from '../NodeLists';

const useStyles = makeStyles((theme) => ({
  panel: {
    background: 'rgba(10, 14, 30, 0.88)',
    border: '1px solid rgba(77, 121, 255, 0.22)',
    borderRadius: 14,
    padding: 14,
    textAlign: 'left',
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      padding: 12,
    },
  },
  title: {
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: '#9ea4d8',
    marginBottom: 10,
  },
  divider: {
    background: 'rgba(255,255,255,0.08)',
    marginBottom: 12,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 10,
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  card: {
    borderRadius: 12,
    border: '1px solid rgba(77, 121, 255, 0.18)',
    background: 'rgba(12, 18, 38, 0.85)',
    padding: '12px 12px 10px',
    minHeight: 78,
    boxSizing: 'border-box',
  },
  metricLabel: {
    color: '#9ea4d8',
    fontSize: 11,
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 8,
  },
  metricValue: {
    color: '#e5e7ff',
    fontSize: 18,
    fontWeight: 900,
    lineHeight: 1.2,
  },
  metricMeta: {
    color: '#8d93c9',
    fontSize: 11,
    marginTop: 6,
    lineHeight: 1.35,
  },
  footer: {
    color: '#9ea4d8',
    fontSize: 12,
    lineHeight: 1.45,
    marginTop: 12,
  },
  error: {
    color: '#ffb4b4',
    fontSize: 12,
    lineHeight: 1.45,
    marginTop: 8,
  },
}));

function formatUsd(value) {
  const formatted = formatNodeListPrice(value);
  return formatted || '—';
}

function formatCompactNumber(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 2,
  }).format(n);
}

function getFirstValue(obj, keys) {
  if (!obj) return null;
  for (const key of keys) {
    const value = obj[key];
    if (value !== null && value !== undefined && value !== '') return value;
  }
  return null;
}

function formatFlags(value) {
  if (!value) return '—';
  if (Array.isArray(value)) {
    const filtered = value.filter(Boolean).map((item) => String(item));
    return filtered.length ? filtered.join(', ') : '—';
  }
  return String(value);
}

function formatPriceSourceDebug(debug) {
  if (!debug || typeof debug !== 'object') return '';
  const current = debug.current || null;
  const daily = debug.daily || null;
  const ratio = Number(debug.ratio);
  const ratioText = Number.isFinite(ratio) ? ratio.toFixed(4) : 'n/a';
  const currentPrice = current && Number.isFinite(Number(current.price_usd)) ? formatUsd(current.price_usd) : 'n/a';
  const dailyPrice = daily && Number.isFinite(Number(daily.price_usd)) ? formatUsd(daily.price_usd) : 'n/a';
  const currentSource = current ? [current.price_source_dex, current.price_source_type].filter(Boolean).join(' / ') : 'n/a';
  const dailySource = daily ? [daily.price_source_dex, daily.price_source_type].filter(Boolean).join(' / ') : 'n/a';
  const currentPool = current && current.price_source_pool ? String(current.price_source_pool).slice(0, 10) : 'n/a';
  const preferred = debug.preferred || 'current';
  return `pref=${preferred}; ratio=${ratioText}; current=${currentPrice} (${currentSource}, pool ${currentPool}); daily=${dailyPrice} (${dailySource})`;
}

export default function MarketMetricsPanel({ metadata, liquidity, loading, error }) {
  const classes = useStyles();
  const liquidityLatest = liquidity && liquidity.latest ? liquidity.latest : null;
  const poolStats = liquidity && liquidity.pool_stats ? liquidity.pool_stats : null;
  const volume24hUsd = poolStats ? poolStats.volume_24h_usd : null;
  const marketCapUsd = metadata && metadata.market_cap_usd ? Number(metadata.market_cap_usd) : 0;
  const volMcapRatio = (volume24hUsd > 0 && marketCapUsd > 0) ? (volume24hUsd / marketCapUsd) : null;
  const priceSourceNote = metadata && metadata.price_source_adjusted
    ? 'Reconciled to CoinGecko daily snapshot'
    : metadata && metadata.price_source_type
      ? `Source ${String(metadata.price_source_type).replace(/_/g, ' ')}`
      : null;
  const fdvUsd = getFirstValue(metadata, ['fdv_usd', 'fdvUSD', 'fdv']);
  const priceConfidence = getFirstValue(metadata, ['price_confidence', 'priceConfidence'])
    || getFirstValue(liquidityLatest, ['price_confidence', 'priceConfidence']);
  const fragilityScore = getFirstValue(metadata, ['fragility_score', 'fragilityScore'])
    || getFirstValue(liquidityLatest, ['fragility_score', 'fragilityScore']);
  const anomalyFlags = getFirstValue(metadata, ['anomaly_flags', 'anomalyFlags'])
    || getFirstValue(liquidityLatest, ['anomaly_flags', 'anomalyFlags']);
  const metrics = [
    {
      label: 'Price',
      value: formatUsd(metadata && metadata.price_usd),
      meta: priceSourceNote || (metadata && metadata.symbol ? metadata.symbol : 'No symbol available'),
    },
    {
      label: 'Market Cap',
      value: formatUsd(metadata && metadata.market_cap_usd),
      meta: metadata && metadata.chain ? metadata.chain : 'Unknown chain',
    },
    {
      label: 'FDV',
      value: formatUsd(fdvUsd),
      meta: fdvUsd ? 'Fully diluted valuation from current metadata' : 'FDV not available',
    },
    {
      label: 'Liquidity',
      value: formatUsd(liquidityLatest && liquidityLatest.liquidity_usd),
      meta: liquidityLatest && liquidityLatest.price_source_dex
        ? `Source ${liquidityLatest.price_source_dex}${liquidityLatest.price_source_type ? ` (${liquidityLatest.price_source_type})` : ''}`
        : 'No liquidity source',
    },
    {
      label: 'Vol 24H',
      value: volume24hUsd > 0 ? formatUsd(volume24hUsd) : '—',
      meta: volume24hUsd > 0 ? `From ${poolStats && poolStats.pool ? poolStats.pool.slice(0, 10) + '…' : 'pool stats'}` : 'No 24H volume data',
    },
    {
      label: 'Vol / MCap',
      value: volMcapRatio !== null ? `${(volMcapRatio * 100).toFixed(1)}%` : '—',
      meta: volMcapRatio !== null ? (volMcapRatio < 0.01 ? 'Very thin — low trading relative to cap' : volMcapRatio > 0.5 ? 'High turnover' : 'Normal trading activity') : 'Unavailable',
    },
    {
      label: 'Price Confidence',
      value: priceConfidence ? String(priceConfidence) : '—',
      meta: 'Confidence on the latest price snapshot',
    },
    {
      label: 'Fragility',
      value: fragilityScore !== null && fragilityScore !== undefined && fragilityScore !== '' ? String(fragilityScore) : '—',
      meta: 'Lower is stronger; higher suggests thinner, more fragile structure',
    },
    {
      label: 'Anomalies',
      value: formatFlags(anomalyFlags),
      meta: 'Flags attached to the current pricing snapshot',
    },
    {
      label: 'Supply',
      value: formatCompactNumber(metadata && metadata.supply),
      meta: metadata && metadata.version ? `Version ${metadata.version}` : 'No version data',
    },
  ];

  if (metadata && metadata.price_source_adjusted && metadata.price_source_debug) {
    metrics.push({
      label: 'Price Source Debug',
      value: metadata.price_source_reason ? String(metadata.price_source_reason) : 'adjusted',
      meta: formatPriceSourceDebug(metadata.price_source_debug) || 'No debug comparison available',
    });
  }

  return (
    <Box className={classes.panel}>
      <Typography className={classes.title}>Market Metrics</Typography>
      <Divider className={classes.divider} />
      <Box className={classes.grid}>
        {loading && !metadata ? (
          Array.from({ length: 10 }).map((_, i) => (
            <Box key={i} className={classes.card}>
              <Skeleton variant="text" width={60} height={14} />
              <Skeleton variant="text" width={110} height={28} style={{ marginTop: 4 }} />
              <Skeleton variant="text" width={140} height={14} style={{ marginTop: 4 }} />
            </Box>
          ))
        ) : metrics.map((metric) => (
          <Box key={metric.label} className={classes.card}>
            <Typography className={classes.metricLabel}>{metric.label}</Typography>
            <Typography className={classes.metricValue}>{metric.value || '—'}</Typography>
            <Typography className={classes.metricMeta}>{metric.meta || 'No additional context'}</Typography>
          </Box>
        ))}
      </Box>
      <Typography className={classes.footer}>
        {loading
          ? 'Loading metadata and liquidity…'
          : liquidityLatest && liquidityLatest.updated_at
            ? `Liquidity updated at ${liquidityLatest.updated_at}`
            : 'Showing the latest available token metadata snapshot.'}
      </Typography>
      {error ? <Typography className={classes.error}>{error}</Typography> : null}
    </Box>
  );
}
